import { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Compass,
  MessageSquare,
  Settings,
  MoreHorizontal,
  Pencil,
  Trash2,
  Notebook,
  Building2,
  Shield,
  Route,
  UserCog,
  ChevronRight,
  RefreshCw,
  Search,
  Download,
  AlertCircle,
  Loader2,
  BookOpen,
  Mail,
  Key,
  LayoutDashboard,
  Users,
  History,
  UsersRound,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarInput,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { buildApiUrl, secureFetch } from "@/lib/queryClient";
import {
  Session,
  categorizeError,
  SessionError,
  formatSessionTimestamp,
} from "@/types/session";
import { Button } from "@/components/ui/button";
import { chatSession } from "@/lib/chat/sessionContract";

const AI_CHAT_ENABLED = import.meta.env.VITE_FEATURE_AI_CHAT === 'true';

const CREATOR_ALLOWED_USER_ID =
  import.meta.env.VITE_CREATOR_ALLOWED_USER_ID || "50060165";

export function AppSidebar() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState<SessionError | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [renamingSession, setRenamingSession] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deletingSession, setDeletingSession] = useState<string | null>(null);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, canManageOrganizations, isSuperAdmin, platformRoles } =
    useAuth();

  const location = useLocation();
  const { pathname } = location;
  const activeSessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('session');
  }, [location.search]);

  const isCreatorAllowed =
    user && String(user.id) === CREATOR_ALLOWED_USER_ID;
  const canAccessPlatformAdmin =
    platformRoles?.isPlatformAdmin || isSuperAdmin;
  const canAccessCreatorStudio =
    platformRoles?.isPlatformStaff || isCreatorAllowed;

  const fetchSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    setSessionsError(null);
    try {
      const result = await chatSession.list();
      if (!result.ok) {
        const errorResult = result as { ok: false; error: SessionError };
        setSessionsError(errorResult.error);
        setSessions([]);
        return;
      }
      const successResult = result as { ok: true; data: { sessions: Session[] } };
      setSessions(successResult.data.sessions);
      setSessionsError(null);
    } catch (error: unknown) {
      console.error("Error fetching sessions:", error);
      const sessionError = categorizeError(error);
      setSessionsError(sessionError);
      setSessions([]);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchSessions();
    }

    const interval = setInterval(() => {
      if (user && !sessionsError) {
        fetchSessions();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [user, fetchSessions, sessionsError]);

  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) {
      return showAllSessions ? sessions : sessions.slice(0, 10);
    }
    const query = searchQuery.toLowerCase();
    const filtered = sessions.filter((s) =>
      s.title.toLowerCase().includes(query),
    );
    return showAllSessions ? filtered : filtered.slice(0, 10);
  }, [sessions, searchQuery, showAllSessions]);

  const hasMoreSessions =
    sessions.length > 10 && !showAllSessions && !searchQuery;

  const handleSessionClick = useCallback(
    (sessionId: string) => {
      setLoadingSessionId(sessionId);
      const targetUrl = `/chat?session=${sessionId}`;
      navigate(targetUrl);
      requestAnimationFrame(() => {
        setLoadingSessionId(null);
      });
    },
    [navigate],
  );

  const createNewSession = async () => {
    try {
      const result = await chatSession.create("New Chat");
      
      if (!result.ok) {
        toast({
          title: "Error",
          description: "Failed to create new session. Please try again.",
          variant: "destructive",
        });
        return;
      }

      navigate(`/chat?session=${result.data.session.id}`);
      fetchSessions();
    } catch (error: unknown) {
      console.error("Create session error:", error);
      toast({
        title: "Error",
        description: "Failed to create new session. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRenameSession = async (sessionId: string) => {
    if (!renameValue.trim()) return;

    try {
      const response = await secureFetch(
        buildApiUrl(`/api/chat/sessions/${sessionId}`),
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache, no-store, must-revalidate",
          },
          body: JSON.stringify({ title: renameValue.trim() }),
        },
      );

      if (!response.ok) throw new Error("Failed to rename session");

      toast({
        title: "Session renamed",
        description: "Your session has been updated",
      });

      setRenamingSession(null);
      setRenameValue("");
      fetchSessions();
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: "Failed to rename session",
        variant: "destructive",
      });
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      const response = await secureFetch(
        buildApiUrl(`/api/chat/sessions/${sessionId}`),
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
          },
        },
      );

      if (!response.ok) throw new Error("Failed to delete session");

      toast({
        title: "Session deleted",
        description: "The session has been permanently removed",
      });

      setDeletingSession(null);
      fetchSessions();
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: "Failed to delete session",
        variant: "destructive",
      });
    }
  };

  const handleExportAllSessions = async () => {
    try {
      const exportData = {
        exportedAt: new Date().toISOString(),
        exportVersion: 1,
        totalSessions: sessions.length,
        sessions: sessions.map((s) => ({
          id: s.id,
          title: s.title,
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
          version: s.version,
        })),
      };

      const jsonContent = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonContent], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `all-sessions-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Sessions Exported",
        description: `Exported ${sessions.length} sessions`,
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Could not export sessions",
        variant: "destructive",
      });
    }
  };

  const startRename = (session: Session) => {
    setRenamingSession(session.id);
    setRenameValue(session.title);
  };

  const adminSubItems = [
    {
      title: "Overview",
      url: "/admin/overview",
      icon: LayoutDashboard,
      testId: "button-admin-overview",
    },
    {
      title: "Access Keys",
      url: "/admin/access-keys",
      icon: Key,
      testId: "button-admin-access-keys",
    },
    {
      title: "User Management",
      url: "/admin/users",
      icon: Users,
      testId: "button-admin-users",
    },
    {
      title: "Support Tickets",
      url: "/admin/feedback",
      icon: MessageSquare,
      testId: "button-admin-feedback",
    },
    {
      title: "Pathway Management",
      url: "/admin/pathways",
      icon: Route,
      testId: "button-admin-pathways",
    },
    {
      title: "Pathway Exposure",
      url: "/admin/pathway-exposure",
      icon: Settings,
      testId: "button-admin-exposure",
    },
    {
      title: "Organizations",
      url: "/admin/organizations",
      icon: Building2,
      testId: "button-admin-organizations",
    },
    {
      title: "Org Invites",
      url: "/admin/org-invites",
      icon: Mail,
      testId: "button-admin-org-invites",
    },
    {
      title: "Platform Roles",
      url: "/admin/roles",
      icon: UserCog,
      testId: "button-admin-roles",
    },
    {
      title: "Modal Templates",
      url: "/admin/modal-templates",
      icon: MessageSquare,
      testId: "button-admin-modal-templates",
    },
  ];

  return (
    <>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <WorkspaceSwitcher />
        </SidebarHeader>

        <SidebarContent>
          {/* Learn Section */}
          <SidebarGroup>
            <SidebarGroupLabel>Learn</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={pathname === "/dashboard"} tooltip="Dashboard" data-testid="button-dashboard">
                    <NavLink to="/dashboard">
                      <LayoutDashboard className="size-4" />
                      <span>Dashboard</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={pathname === "/team-hub"} tooltip="Team Hub" data-testid="button-team-hub">
                    <NavLink to="/team-hub">
                      <UsersRound className="size-4" />
                      <span>Team Hub</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={pathname === "/my-learning"} tooltip="My Learning" data-testid="button-my-learning">
                    <NavLink to="/my-learning">
                      <BookOpen className="size-4" />
                      <span>My Learning</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={pathname === "/pathways"} tooltip="Pathways" data-testid="button-pathways">
                    <NavLink to="/pathways">
                      <Route className="size-4" />
                      <span>Pathways</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={pathname === "/explore"} tooltip="Explore" data-testid="button-explore">
                    <NavLink to="/explore">
                      <Compass className="size-4" />
                      <span>Explore</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Tools Section */}
          {user && (
            <SidebarGroup>
              <SidebarGroupLabel>Tools</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {AI_CHAT_ENABLED && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        tooltip="New Chat"
                        onClick={createNewSession}
                        data-testid="button-new-session"
                      >
                        <MessageSquare className="size-4" />
                        <span>New Chat</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}

                  {AI_CHAT_ENABLED && (
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild isActive={pathname === "/chat/history"} tooltip="Chat History" data-testid="button-chat-history">
                        <NavLink to="/chat/history">
                          <History className="size-4" />
                          <span>Chat History</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}

                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={pathname === "/notes"} tooltip="Notes" data-testid="button-notes">
                      <NavLink to="/notes">
                        <Notebook className="size-4" />
                        <span>Notes</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>

                  {canManageOrganizations && (
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild isActive={pathname === "/organizations"} tooltip="Organizations" data-testid="button-organizations">
                        <NavLink to="/organizations">
                          <Building2 className="size-4" />
                          <span>Organizations</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {/* Creator Section */}
          {canAccessCreatorStudio && (
            <SidebarGroup>
              <SidebarGroupLabel>Create</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={pathname === "/creator"} tooltip="Creator Studio" data-testid="button-creator">
                      <NavLink to="/creator">
                        <Pencil className="size-4" />
                        <span>Creator Studio</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {/* Admin Section */}
          {canAccessPlatformAdmin && (
            <Collapsible
              open={adminExpanded}
              onOpenChange={setAdminExpanded}
              className="group/collapsible"
            >
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger
                    className="flex w-full items-center"
                    data-testid="button-admin-toggle"
                  >
                    <Shield className="size-4 mr-2" />
                    <span>Admin</span>
                    <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      <SidebarMenuItem>
                        <SidebarMenuSub>
                          {adminSubItems.map((item) => (
                            <SidebarMenuSubItem key={item.url}>
                              <SidebarMenuSubButton asChild isActive={pathname === item.url}>
                                <NavLink to={item.url} data-testid={item.testId}>
                                  <item.icon className="size-4" />
                                  <span>{item.title}</span>
                                </NavLink>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          )}

          {/* Recent Sessions Section */}
          {user && AI_CHAT_ENABLED && (
            <SidebarGroup className="group-data-[collapsible=icon]:hidden">
              <SidebarGroupLabel>
                <span className="flex-1">Recent</span>
                <div className="flex items-center gap-1">
                  {sessions.length > 0 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5"
                      onClick={handleExportAllSessions}
                      data-testid="button-export-all-sessions"
                    >
                      <Download className="size-3" />
                    </Button>
                  )}
                  {sessionsError?.retryable && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5"
                      onClick={fetchSessions}
                      data-testid="button-retry-sessions"
                    >
                      <RefreshCw className="size-3" />
                    </Button>
                  )}
                </div>
              </SidebarGroupLabel>
              <SidebarGroupContent>
                {sessions.length > 3 && (
                  <div className="px-2 pb-2">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3 text-muted-foreground" />
                      <SidebarInput
                        type="text"
                        placeholder="Search sessions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-7 text-xs"
                        data-testid="input-search-sessions"
                      />
                    </div>
                  </div>
                )}

                {isLoadingSessions && sessions.length === 0 ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-xs text-muted-foreground">
                      Loading sessions...
                    </span>
                  </div>
                ) : sessionsError ? (
                  <div className="px-3 py-4 text-center">
                    <AlertCircle className="size-5 mx-auto text-destructive mb-2" />
                    <p className="text-xs text-muted-foreground mb-2">
                      {sessionsError.message}
                    </p>
                    {sessionsError.retryable && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchSessions}
                        className="text-xs"
                        data-testid="button-retry-sessions-error"
                      >
                        <RefreshCw className="size-3 mr-1" />
                        Retry
                      </Button>
                    )}
                  </div>
                ) : filteredSessions.length > 0 ? (
                  <SidebarMenu>
                    {filteredSessions.map((session) => (
                      <SidebarMenuItem key={session.id}>
                        <SidebarMenuButton
                          onClick={() => handleSessionClick(session.id)}
                          className={
                            loadingSessionId === session.id ? "opacity-50" : ""
                          }
                          data-testid={`button-session-${session.id}`}
                          data-active={session.id === activeSessionId}
                        >
                          {loadingSessionId === session.id ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <MessageSquare className="size-4" />
                          )}
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="truncate text-sm">
                              {session.title}
                            </span>
                            <span className="truncate text-xs text-muted-foreground">
                              {formatSessionTimestamp(session.updatedAt)}
                            </span>
                          </div>
                        </SidebarMenuButton>
                        <SidebarMenuAction showOnHover asChild>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`button-session-menu-${session.id}`}
                              >
                                <MoreHorizontal className="size-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-48"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startRename(session);
                                }}
                              >
                                <Pencil className="size-4 mr-2" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingSession(session.id);
                                }}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="size-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </SidebarMenuAction>
                      </SidebarMenuItem>
                    ))}

                    {hasMoreSessions && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => setShowAllSessions(true)}
                          className="text-xs text-muted-foreground"
                          data-testid="button-show-more-sessions"
                        >
                          <span>Show all {sessions.length} sessions</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                    {showAllSessions && sessions.length > 10 && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => setShowAllSessions(false)}
                          className="text-xs text-muted-foreground"
                          data-testid="button-show-less-sessions"
                        >
                          <span>Show less</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                  </SidebarMenu>
                ) : (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    {searchQuery
                      ? "No sessions match your search"
                      : "No recent sessions"}
                  </p>
                )}
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>
      </Sidebar>

      {/* Rename Dialog */}
      <AlertDialog
        open={renamingSession !== null}
        onOpenChange={() => setRenamingSession(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rename Session</AlertDialogTitle>
            <AlertDialogDescription>
              Enter a new name for this session
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Session name"
            onKeyDown={(e) => {
              if (e.key === "Enter" && renamingSession) {
                handleRenameSession(renamingSession);
              }
            }}
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRenamingSession(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                renamingSession && handleRenameSession(renamingSession)
              }
            >
              Rename
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deletingSession !== null}
        onOpenChange={() => setDeletingSession(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this session? This will
              permanently remove all messages and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingSession(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deletingSession && handleDeleteSession(deletingSession)
              }
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
